/**
 * Configuración centralizada de variables de entorno
 * Tipado seguro y validación de variables requeridas
 */

interface EnvConfig {
  apiUrl: string;
  env: "development" | "production" | "test";
  appName: string;
  appVersion: string;
  enableAnalytics: boolean;
  enableDebug: boolean;
}

/**
 * Valida que una variable de entorno exista
 */
function getEnvVar(key: string, fallback?: string): string {
  const value = import.meta.env[key] || fallback;

  if (!value) {
    throw new Error(`Variable de entorno requerida no encontrada: ${key}`);
  }

  return value;
}

/**
 * Convierte string a boolean
 */
function parseBoolean(value: string | undefined, fallback: boolean): boolean {
  if (!value) return fallback;
  return value.toLowerCase() === "true";
}

/**
 * Configuración de la aplicación
 */
export const env: EnvConfig = {
  // API Configuration
  apiUrl: getEnvVar("VITE_API_URL", "http://localhost:4000/api"),

  // Environment
  env: (import.meta.env.MODE as EnvConfig["env"]) || "development",

  // App Configuration
  appName: getEnvVar("VITE_APP_NAME", "Mi Aplicación"),
  appVersion: getEnvVar("VITE_APP_VERSION", "1.0.0"),

  // Feature Flags
  enableAnalytics: parseBoolean(import.meta.env.VITE_ENABLE_ANALYTICS, false),
  enableDebug: parseBoolean(import.meta.env.VITE_ENABLE_DEBUG, true),
};

/**
 * Helper para verificar el entorno actual
 */
export const isDevelopment = env.env === "development";
export const isProduction = env.env === "production";
export const isTest = env.env === "test";

/**
 * Log de configuración en desarrollo
 */
if (isDevelopment && env.enableDebug) {
  console.log("🔧 Environment Configuration:", {
    ...env,
    mode: import.meta.env.MODE,
  });
}

export default env;
